import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "dropdown", "crefonummer", "name", "plz", "ort", "strasseHausnummer"]

  // -1 stands for no option selected
  hoveredOptionIndex = -1;
  data = [];

  connect() {
    if (this.hasInputTarget) {
      // Wrap the `search` method with debounce
      this.search = this.debounce(this.search.bind(this), 200);

      // initial resizing of the dropdown menu to match the input width
      this.dropdownTarget.querySelector('.dropdown-menu').style.minWidth = `${this.inputTarget.clientWidth}px`;
      window.addEventListener('resize', () => {
        this.dropdownTarget.querySelector('.dropdown-menu').style.minWidth = `${this.inputTarget.clientWidth}px`;
      })

      document.addEventListener("click", this.handleClickOutside.bind(this))
      this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
    this.inputTarget.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  // Debounced search method
  search() {
    this.fetchResults(this.inputTarget.value)
  }

  async fetchResults(query) {
    if (query.trim() === "") {
      this.hideResults()
      return
    }

    const response = await fetch(`/smart_sign_up?search_string=${encodeURIComponent(query)}`)
    this.data = await response.json()
    this.hoveredOptionIndex = -1;

    this.resultsTarget.innerHTML = this.renderResults(this.data)
    this.showResults()

    // after fetching all data the listeners are added to each item
    // to combine the mouse select with arrow au and down select options
    const items = this.dropdownTarget.querySelectorAll('.dropdown .dropdown-menu .dropdown-item');
    for (let i = 0; i < items.length; i++) {
      items[i].addEventListener('mouseover', this.handleDropdownMouseHover.bind(this, i))
      items[i].addEventListener('click', this.selectHoveredOption.bind(this))
    }
  }

  renderResults(data) {
    return data.map(item => `
      <a class="dropdown-item ${item.isSelected ? 'selected' : ''}" data-action="click->smart-sign-up#selectResult"
         data-crefonummer="${item.crefonummer}"
         data-identnummer="${item.identnummer}"
         data-name="${item.name}"
         data-plz="${item.plz}"
         data-ort="${item.ort}"
         data-strasseHausnummer="${item.strasseHausnummer}">
        ${item.name} - ${item.ort}
      </a>
    `).join("")
  }

  invalidateResult() {
    this.crefonummerTarget.value = '';
    this.nameTargets.forEach(input => input.value = '');
    this.plzTarget.value = '';
    this.ortTarget.value = '';
    this.strasseHausnummerTarget.value = '';
    this.sendInputEvents();
  }

  selectResult(event) {
    const item = event.currentTarget;
    this.crefonummerTarget.value = item.dataset.crefonummer;
    this.nameTargets.forEach(input => input.value = item.dataset.name);
    this.plzTarget.value = item.dataset.plz;
    this.ortTarget.value = item.dataset.ort;
    this.strasseHausnummerTarget.value = item.dataset.strassehausnummer;

    this.hideResults();
    this.sendInputEvents();
    this.dispatch("resultSelected", { detail: { companyName: item.dataset.name, crefonummer: item.dataset.crefonummer } })
  }

  sendInputEvents() {
    this.crefonummerTarget.dispatchEvent(new Event('input'))
    this.nameTargets.forEach(input => input.dispatchEvent(new Event('input')))
    this.plzTarget.dispatchEvent(new Event('input'))
    this.ortTarget.dispatchEvent(new Event('input'))
    this.strasseHausnummerTarget.dispatchEvent(new Event('input'))
    this.crefonummerTarget.dispatchEvent(new Event('blur'))
    this.nameTargets.forEach(input => input.dispatchEvent(new Event('blur')))
    this.plzTarget.dispatchEvent(new Event('blur'))
    this.ortTarget.dispatchEvent(new Event('blur'))
    this.strasseHausnummerTarget.dispatchEvent(new Event('blur'))
  }

  handleKeydown(event) {
    if (!this.isActive()) {
      return;
    }

    switch (event.key) {
      case "Escape":
        this.hideResults();
        break;
      case "Up":
      case "ArrowUp":
        this.hoverPrevOption();
        break;
      case "Down":
      case "ArrowDown":
        this.hoverNextOption();
        break;
      case "Enter":
        this.selectHoveredOption();
        break;
    }
  }

  handleClickOutside(event) {
    if (!this.inputTarget.contains(event.target) && !this.resultsTarget.contains(event.target) && !this.dropdownTarget.contains(event.target)) {
      this.hideResults()
    }
  }

  handleDropdownMouseHover(index, event) {
    this.hoveredOptionIndex = index
    this.hoverOption()
  }

  showResults() {
    this.dropdownTarget.classList.add("is-active")
  }

  hideResults() {
    this.dropdownTarget.classList.remove("is-active")
  }

  hoverPrevOption() {
    const dataLength = this.data.length

    if (dataLength === 0) {
      return;
    }

    // if none selected or in the beginning of the list select last option
    if (this.hoveredOptionIndex === -1 || this.hoveredOptionIndex === 0) {
      this.hoveredOptionIndex = dataLength - 1
    } else {
      this.hoveredOptionIndex -= 1
    }

    this.hoverOption()
  }

  hoverNextOption() {
    const dataLength = this.data.length

    if (dataLength === 0) {
      return;
    }

    // if none selected or in the end of list select the first option
    if (this.hoveredOptionIndex === -1 || this.hoveredOptionIndex + 1 === dataLength) {
      this.hoveredOptionIndex = 0
    } else {
      this.hoveredOptionIndex += 1
    }

    this.hoverOption()
  }

  hoverOption() {
    this.removeOptionHover()

    this.dropdownTarget.querySelector(`a.dropdown-item:nth-child(${this.hoveredOptionIndex + 1})`).classList.add('selected')
  }

  removeOptionHover() {
    const currentlySelected = this.dropdownTarget.querySelector('a.dropdown-item.selected')

    if (currentlySelected) {
      currentlySelected.classList.remove('selected')
    }
  }

  selectHoveredOption() {
    if (this.hoveredOptionIndex === -1) {
      return;
    }

    const item = this.data[this.hoveredOptionIndex]
    this.crefonummerTarget.value = item.crefonummer;
    this.nameTargets.forEach(input => input.value = item.name);
    this.plzTarget.value = item.plz;
    this.ortTarget.value = item.ort;
    this.strasseHausnummerTarget.value = item.strasseHausnummer;

    this.hideResults();
    this.sendInputEvents();
    this.dispatch("resultSelected", { detail: { companyName: item.name, crefonummer: item.crefonummer } })

    this.inputTarget.value = item.name;
    this.inputTarget.blur();
  }

  isActive() {
    return this.dropdownTarget.classList.value.includes('is-active')
  }

  // Debounce helper function
  debounce(func, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer); // Clear any previous timer on new input
      timer = setTimeout(() => func(...args), delay); // Set new timer for 200ms
    };
  }
}
